import { useState } from "react";
import PageHeader from "@/components/shared/PageHeader";
import CTASection from "@/components/shared/CTASection";
import { SDGS_ANCHOR, SDGS_WIDER } from "@/data/content";
import { SDG } from "@/constants/testIds";

const ALL_SDGS = [
  ...SDGS_ANCHOR.map((s) => ({ ...s, anchor: true })),
  ...SDGS_WIDER.map((s) => ({ ...s, anchor: false })),
].sort((a, b) => a.n - b.n);

export default function SDGsPage() {
  const [selected, setSelected] = useState(SDGS_ANCHOR[0]);

  return (
    <>
      <PageHeader
        overline="Sustainable Development Goals"
        title="Resilience expressed across the 2030 Agenda."
        lead="Resilience touches many of the seventeen goals, with climate action and sustainable cities most directly engaged. Select a tile to see how the Centre engages each goal."
      />

      <section className="ccdr-section">
        <div className="ccdr-container">
          {/* Anchor goals */}
          <p className="ccdr-eyebrow mb-6">The two most directly engaged</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16">
            {SDGS_ANCHOR.map((g) => (
              <button
                key={g.n}
                onClick={() => setSelected({ ...g, anchor: true })}
                className="text-left p-8 bg-[#0B4F6C] text-white hover:bg-[#053043] transition-colors"
                data-testid={SDG.tile(g.n)}
              >
                <div className="flex items-center justify-between mb-4">
                  <span
                    className="text-5xl"
                    style={{ color: "var(--ccdr-accent)", fontWeight: 300 }}
                  >
                    {g.n}
                  </span>
                  <span
                    className="text-xs uppercase tracking-[0.22em] text-[#E9C46A]"
                  >
                    Anchor Goal
                  </span>
                </div>
                <h3
                  className="text-xl text-white mb-3"
                  style={{ fontWeight: 500 }}
                >
                  {g.name}
                </h3>
                <p className="text-[#CBD5E1] text-sm leading-relaxed">{g.body}</p>
              </button>
            ))}
          </div>

          {/* Grid + detail panel */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-8">
              <p className="ccdr-eyebrow mb-6">The wider resilience map</p>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
                {ALL_SDGS.map((g) => {
                  const isSelected = selected.n === g.n;
                  return (
                    <button
                      key={g.n}
                      onClick={() => setSelected(g)}
                      data-testid={SDG.tile(g.n)}
                      className={`sdg-tile ${g.anchor ? "anchor" : ""} ${isSelected ? "selected" : ""}`}
                      aria-label={`SDG ${g.n} ${g.name}`}
                    >
                      <span
                        className="text-3xl tabular-nums"
                        style={{ fontWeight: 300 }}
                      >
                        {g.n}
                      </span>
                      <h4
                        className="text-[11px] leading-tight uppercase tracking-wider"
                        style={{ color: g.anchor ? "#fff" : "var(--ccdr-primary-dark)" }}
                      >
                        {g.name}
                      </h4>
                    </button>
                  );
                })}
              </div>
            </div>

            <aside className="lg:col-span-4">
              <div data-testid={SDG.detail} className="border border-[#E5E7EB] bg-white p-8 sticky top-28">
                <span
                  className="text-xs uppercase tracking-[0.22em]"
                  style={{ color: "var(--ccdr-accent-dark)" }}
                >
                  Goal {selected.n}
                </span>
                <h3 className="ccdr-h3 mt-3 mb-4">{selected.name}</h3>
                <p className="text-[#4B5563] leading-relaxed">{selected.body}</p>
              </div>
            </aside>
          </div>
        </div>
      </section>

      <CTASection
        title="See how a focus area advances a specific goal, or partner with the Centre on goal-aligned work."
        primary={{ to: "/get-involved", label: "Partner with us" }}
        secondary={{ to: "/focus-areas", label: "Or open the focus areas" }}
      />
    </>
  );
}
